/**
 * Utility functions for validation and null checking
 */

/**
 * Checks if a value is null, undefined, or an empty string
 * @param value - The value to check
 * @returns true if the value is null, undefined, or an empty string, false otherwise
 */
export const isNullEmptyOrUndefined = (value: unknown): boolean => {
  return value === null || value === undefined || value === '';
};

/**
 * Normalizes fund_type to ensure it's always an object, never a string representation of JSON.
 * This prevents the bug where fund_type gets stored as a stringified JSON which crashes analysis.
 *
 * @param fundType - The fund_type value which could be an object, JSON string, or null
 * @returns The normalized fund_type as an object, or null if invalid/empty
 * @throws Error if the value is a string that cannot be parsed as JSON
 */
export const normalizeFundType = (
  fundType: unknown,
): Record<string, unknown> | null => {
  if (fundType === null || fundType === undefined) {
    return null;
  }

  // If it's already an object (but not an array), return it
  if (
    typeof fundType === 'object' &&
    !Array.isArray(fundType) &&
    fundType !== null
  ) {
    return fundType as Record<string, unknown>;
  }

  // If it's a string, try to parse it as JSON
  if (typeof fundType === 'string') {
    const trimmed = fundType.trim();
    if (trimmed === '') {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);

      // Ensure the parsed result is an object, not a primitive or array
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        throw new Error(
          `fund_type must be a JSON object, got ${Array.isArray(parsed) ? 'array' : typeof parsed}`,
        );
      }

      return parsed as Record<string, unknown>;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(
          `fund_type is a string but not valid JSON: "${trimmed.substring(0, 50)}${trimmed.length > 50 ? '...' : ''}"`,
        );
      }
      throw error;
    }
  }

  throw new Error(
    `fund_type must be an object or JSON string, got ${typeof fundType}`,
  );
};

/**
 * Normalizes portfolio changes before sending to the database.
 * Specifically handles fund_type to ensure it's never stored as a string.
 *
 * @param changes - The portfolio changes object
 * @returns The normalized changes with fund_type as an object
 */
/**
 * Validates an ISIN (International Securities Identification Number).
 * Format: 2 letter country code + 9 alphanumeric chars + 1 check digit
 * Uses the Luhn algorithm on the converted digit string.
 */
export const isValidISIN = (value: string): boolean => {
  const isin = value.trim().toUpperCase();
  if (isin.length !== 12) return false;
  if (!/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(isin)) return false;

  // Convert letters to numbers (A=10, B=11, ..., Z=35) and concat all digits
  const digits = isin
    .split('')
    .map((ch) => {
      const code = ch.charCodeAt(0);
      return code >= 65 ? (code - 55).toString() : ch;
    })
    .join('');

  // Luhn mod-10 check
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
};

export const normalizePortfolioChanges = <T extends { fund_type?: unknown }>(
  changes: T,
): T => {
  if (!('fund_type' in changes)) {
    return changes;
  }

  return {
    ...changes,
    fund_type: normalizeFundType(changes.fund_type),
  };
};
